/**
 * Browser side of the CSV export — produces a file download.
 *
 * Kept separate from {@link ./buildCsv} so the pure string-building logic can be tested and reused
 * without pulling in DOM dependencies.
 */

import { buildCsv } from './buildCsv';
import type { ColumnDef, DownloadCsvOptions } from './types';

/** Lifetime (ms) we keep an iOS blob URL alive so the user has time to preview + share. */
const IOS_BLOB_URL_TTL_MS = 30_000;

/**
 * Desktop Safari (and other WebKit macOS browsers that are not Chrome-based) sometimes starts the
 * blob download asynchronously; revoking on the next microtask can cancel it before the file is
 * handed to the download UI. A short delay avoids empty / failed downloads. Chrome's UA also
 * contains "Safari", so we exclude Chrome/Chromium/Edge/Opera explicitly.
 */
const WEBKIT_DESKTOP_BLOB_REVOKE_DELAY_MS = 2_000;

/** iPhone / iPod / iPad / iPadOS “desktop” UA — `<a download>` on blob URLs is unreliable on iOS. */
function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !('MSStream' in window)) {
    return true;
  }
  // iPadOS 13+ reports a macOS UA. Distinguish real Macs from iPads by the touch-point count.
  if (
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  ) {
    return true;
  }
  return false;
}

function isLikelyWebKitBlobDownloadBrowser(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  if (!/Safari/i.test(ua)) {
    return false;
  }
  if (/(Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|FxiOS)/i.test(ua)) {
    return false;
  }
  // iOS is handled by the `window.open(blob)` path — never hit anchor + revoke timing here.
  return !isIOSDevice();
}

export function downloadCsv<TRow>(
  rows: readonly TRow[],
  columns: readonly ColumnDef<TRow>[],
  options: DownloadCsvOptions
): void {
  if (typeof document === 'undefined' || typeof URL === 'undefined') {
    throw new Error(
      'downloadCsv: no DOM available — call buildCsv directly from non-browser code'
    );
  }

  const { filename, ...buildOptions } = options;

  // BOM defaults to true for downloads — without it Excel mis-detects UTF-8 and mojibake any
  // accented or CJK characters.
  const content = buildCsv(rows, columns, { bom: true, ...buildOptions });

  const blob = new Blob([content], {
    type: 'text/csv;charset=utf-8'
  });

  const url = URL.createObjectURL(blob);

  // All iOS Safari (iPhone, iPod, iPad, iPadOS “Mac” UA): `<a download>` on blob URLs is flaky;
  // open the blob in a new tab so the user can Share → Save to Files (or download from the tab).
  if (isIOSDevice()) {
    const opened = window.open(url, '_blank');
    if (!opened) {
      // Popup blocker denied the new tab — last resort is a same-window navigation. Unpleasant
      // but always works. Happens in rare configurations (content blockers, iOS 17 strict mode).
      window.location.href = url;
    }
    // Hold the URL longer on iOS — Safari reads it asynchronously after the tab opens and will
    // error out if we revoke immediately.
    setTimeout(() => URL.revokeObjectURL(url), IOS_BLOB_URL_TTL_MS);
    return;
  }

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = ensureCsvExtension(filename);
  anchor.rel = 'noopener';
  // Safari has historically ignored `download` on anchors with `display: none`. Keep the node
  // off-screen but attached and "renderable" so WebKit can commit the navigation/download.
  anchor.setAttribute('aria-hidden', 'true');
  anchor.style.position = 'fixed';
  anchor.style.left = '-9999px';
  anchor.style.top = '0';
  anchor.style.opacity = '0';
  anchor.style.pointerEvents = 'none';

  document.body.appendChild(anchor);
  anchor.click();

  // WebKit (macOS Safari, etc.): defer both DOM cleanup and revoke — removing the anchor or
  // revoking the blob URL too early can cancel the download. Other browsers can clean up on the
  // next task (delay 0).
  const cleanupDelayMs = isLikelyWebKitBlobDownloadBrowser()
    ? WEBKIT_DESKTOP_BLOB_REVOKE_DELAY_MS
    : 0;

  window.setTimeout(() => {
    anchor.remove();
    URL.revokeObjectURL(url);
  }, cleanupDelayMs);
}

/** Normalise filenames users type — strip illegal chars, ensure `.csv` suffix. */
export function ensureCsvExtension(name: string): string {
  const trimmed = (name || '').trim();
  const cleaned = (trimmed || 'export').replace(/[\\/:*?"<>|]/g, '_');
  return /\.csv$/i.test(cleaned) ? cleaned : `${cleaned}.csv`;
}

/**
 * True if the current browser is iOS Safari (including iPad on iOS 13+ which falsely reports a
 * Mac-like UA — we probe `navigator.maxTouchPoints` as a tie-breaker).
 *
 * Exported for testing; callers should not branch on this themselves — let `downloadCsv` own
 * the decision so the iOS fallback stays in one place.
 */
export function isIOS(): boolean {
  return isIOSDevice();
}
